export const APP_CHECK_WAIT_MS = 2_000;
export const APP_CHECK_RETRY_COOLDOWN_MS = 5 * 60_000;

type TokenOutcome =
  { status: "token"; token: string } | { status: "failed" } | { status: "timeout" };

export function createBoundedAppCheckTokenProvider(
  load: () => Promise<string>,
  waitMs = APP_CHECK_WAIT_MS,
  cooldownMs = APP_CHECK_RETRY_COOLDOWN_MS
): () => Promise<string | null> {
  let inFlight: Promise<string | null> | null = null;
  let retryAfter = 0;

  return function getToken() {
    if (Date.now() < retryAfter) return Promise.resolve(null);
    if (inFlight) return inFlight;

    let timer: ReturnType<typeof setTimeout> | undefined;
    const loaded: Promise<TokenOutcome> = Promise.resolve()
      .then(load)
      .then(
        (token) => ({ status: "token", token }),
        () => ({ status: "failed" })
      );
    const deadline = new Promise<TokenOutcome>((resolve) => {
      timer = setTimeout(() => resolve({ status: "timeout" }), waitMs);
    });
    const pending = Promise.race([loaded, deadline])
      .then((outcome) => {
        clearTimeout(timer);
        if (outcome.status === "token") {
          retryAfter = 0;
          return outcome.token;
        }
        retryAfter = Date.now() + cooldownMs;
        return null;
      })
      .finally(() => {
        if (inFlight === pending) inFlight = null;
      });
    inFlight = pending;
    return pending;
  };
}
